import { useState, useId } from 'react'
import type { ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@shared/lib/utils'

/**
 * Shared building blocks used by all detail sheets (Authorization, Clearing, etc.)
 */

interface RowProps {
  label: string
  value?: string
  mono?: boolean
  children?: ReactNode
}

export function Row({ label, value, mono, children }: RowProps) {
  if (!children && !value) return null
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-border/50 last:border-0">
      <span className="text-xs text-muted-foreground shrink-0 w-44">{label}</span>
      {children ? (
        <div className="text-xs text-right">{children}</div>
      ) : (
        <span className={cn('text-xs text-right break-all', mono && 'font-mono')}>{value}</span>
      )}
    </div>
  )
}

interface SectionProps {
  title: string
  children: ReactNode
  defaultOpen?: boolean
}

export function Section({ title, children, defaultOpen = true }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  const contentId = useId()

  return (
    <div className="rounded-lg border bg-card">
      <button
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={contentId}
      >
        {title}
        <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', !open && '-rotate-90')} aria-hidden="true" />
      </button>
      {open && (
        <div id={contentId} className="px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  )
}
